import styled, { css } from 'styled-components';
import { devices } from '../../../../styles/viewports';
import { Colors } from '../../../../types';
import { Grid } from '../../../atoms/Grid';
import { PrimaryButton } from '../../../atoms';

type withInversion = {
  invert: boolean;
  theme: {
    colors: Colors;
  };
};

const BrandingTextBlock = styled.div`
  font-size: var(--font-size-body-2);
  font-family: var(--font-family-header);
  text-transform: uppercase;
  margin: 0;
  white-space: nowrap;
  @media screen and (max-width: 920px) {
    white-space: normal;
  }
  @media ${devices.mobile} {
    white-space: nowrap;
  }
`;

const BrandingTextBody = styled.div`
  font-family: var(--font-family-body);
  font-size: var(--font-size-heading-6);
  line-height: var(--line-height-heading-6);
`;

const ComapctHeaderWrapper = styled.div`
  position: relative;
  display: block;
`;

const ComapctHeaderGrid = styled(Grid)`
  gap: 0 var(--grid-column-gap);
  grid-template-rows: min-content min-content;
`;

const CompactHeaderAssetWrapper = styled.div`
  height: 37.5vw;
  width: 100%;
  position: relative;
  overflow: hidden;
  background-color: #000;
  img {
    position: relative;
    height: 100%;
    width: 100%;
    object-fit: cover;
  }
  video {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    object-fit: contain;
    @media ${devices.mobile} {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      margin: auto;
      height: auto;
      max-height: 100vh;
      width: 100%;
      object-fit: contain;
    }
  }
  @media ${devices.mobile} {
    height: 85vw;
    max-height: 100vh;
  }
`;

const CompactHeaderCopyWrapper = styled(Grid)<withInversion>`
  position: relative;
  height: 160px;
  grid-template-rows: min-content min-content;
  grid-template-rows: min-content min-content;
  grid-template-columns: [logo] 1fr [title] 3fr [cta] 1fr;
  align-content: center;
  gap: 0;
  background-color: ${({ invert, theme }) => (invert ? theme.colors.white : theme.colors.primary)};
  color: ${({ invert, theme }) => (invert ? theme.colors.black : theme.colors.white)};

  svg {
    width: 210px;
    fill: ${({ invert, theme }) => (invert ? theme.colors.black : theme.colors.white)};
  }

  h1::selection,
  div::selection {
    ${({ theme }) =>
      theme.colors.primary === theme.colors.stream &&
      `
      color: ${theme.colors.stream};
      background-color: ${theme.colors.white};
    `};
  }

  @media ${devices.tablet} {
    svg {
      width: 145px;
    }
  }

  @media ${devices.mobile} {
    display: flex;
    flex-direction: column;
    height: auto;
    padding: 32px 20px;

    svg {
      width: 180px;
    }
  }
`;

const CompactHeaderLogoWrapper = styled.div`
  display: flex;
  grid-area: logo;
  padding-left: 50px;
  justify-content: left;
  align-items: center;
  width: 100%;
  @media ${devices.mobile} {
    padding-left: 0px;
    justify-content: center;
  }
  @media ${devices.tablet} {
    width: fit-content;
  }
`;

const CompactHeaderTitleWrapper = styled.div`
  display: flex;
  grid-area: title;
  align-items: center;
  justify-content: center;
  font-family: var(--font-family-header);
  text-transform: uppercase;
  text-align: center;
  padding: 0 2em;
  @media ${devices.mobile} {
    justify-content: center;
  }
`;

const CompactHeaderCTAWrapper = styled.div`
  display: flex;
  grid-area: cta;
  justify-content: right;
  align-items: center;
  padding-right: 50px;
  @media ${devices.mobile} {
    padding-right: 0px;
    justify-content: center;
  }
`;

const PrimaryButtonWithInversion = styled(PrimaryButton)<withInversion>`
  background-color: ${({ invert, theme }) => (invert ? theme.colors.primary : theme.colors.white)};
  color: ${({ invert, theme }) => (invert ? theme.colors.white : theme.colors.black)};
  svg {
    fill: ${({ invert, theme }) => (invert ? theme.colors.white : theme.colors.black)};
    path {
      fill: ${({ invert, theme }) => (invert ? theme.colors.white : theme.colors.black)};
    }
  }
  min-width: 145px;
`;
const PlayButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: #fff;
  border: none;
  padding: 12px;
`;

const VideoControlsWrapper = styled.div`
  position: absolute;
  left: 50px;
  bottom: 24px;
  display: flex;
  align-items: center;
  @media ${devices.mobile} {
    left: 20px;
    bottom: 12px;
  }
`;

const VideoVolume = styled.input`
  width: 100px;
  margin-left: 24px;
`;

const VideoMute = styled.button`
  width: 17px;
  height: 17px;
  background: none;
  border: none;
  padding: 0;
  svg {
    width: 100%;
    height: 100%;
  }
`;

const thumbStyes = css`
  -webkit-appearance: none;
  appearance: none;
  border: none;
  height: 8px;
  width: 8px;
  border-radius: 50%;
  background: #ffffff;
  border: none;
  transform: translateY(-25%);
`;

const trackStyles = css`
  width: 100%;
  cursor: pointer;
  height: 4px;
  cursor: pointer;
  background: rgb(241, 241, 241, 0.5);
  border-radius: 1.3px;
  border: none;
`;

const VolumeControlsWrapper = styled.div`
  display: flex;
  margin: 12px 12px;
  padding: 0;
  background-color: rgb(26, 26, 26, 0.6);
  height: 36px;
  width: 100px;
  align-items: center;
  justify-content: center;

  /* Range slider overrides */
  input[type='range' i] {
    -webkit-appearance: none;
    appearance: none;
    width: 42px;
    margin: 0;
    margin-left: 7px;
    background: transparent; /* Otherwise white in Chrome */

    ::-webkit-slider-runnable-track {
      ${trackStyles}
    }
    ::-moz-range-track {
      ${trackStyles}
    }
    ::-ms-track {
      ${trackStyles}
    }
    ::-ms-fill-lower {
      ${trackStyles}
    }
    ::-ms-fill-upper {
      ${trackStyles}
    }

    ::-webkit-slider-thumb {
      ${thumbStyes}
    }
    ::-moz-range-thumb {
      ${thumbStyes}
      transform: translateY(0);
    }
    ::-ms-thumb {
      ${thumbStyes}
    }
  }
`;

export {
  BrandingTextBlock,
  BrandingTextBody,
  ComapctHeaderWrapper,
  ComapctHeaderGrid,
  CompactHeaderAssetWrapper,
  CompactHeaderCopyWrapper,
  CompactHeaderLogoWrapper,
  CompactHeaderTitleWrapper,
  CompactHeaderCTAWrapper,
  PrimaryButtonWithInversion,
  PlayButton,
  VideoControlsWrapper,
  VideoVolume,
  VideoMute,
  VolumeControlsWrapper,
};
